import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from './auth/AuthProvider'
import { AppShell } from './components/AppShell'
import { LoadingState } from './components/States'
import { LoginPage } from './pages/LoginPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'

const OverviewPage = lazy(() => import('./pages/OverviewPage').then((module) => ({ default: module.OverviewPage })))
const SearchPage = lazy(() => import('./pages/SearchPage').then((module) => ({ default: module.SearchPage })))
const PlotsPage = lazy(() => import('./pages/PlotsPage').then((module) => ({ default: module.PlotsPage })))
const PlotDetailPage = lazy(() => import('./pages/PlotDetailPage').then((module) => ({ default: module.PlotDetailPage })))
const PeoplePage = lazy(() => import('./pages/PeoplePage').then((module) => ({ default: module.PeoplePage })))
const NewRecordPage = lazy(() => import('./pages/NewRecordPage').then((module) => ({ default: module.NewRecordPage })))
const PersonEditPage = lazy(() => import('./pages/PersonEditPage').then((module) => ({ default: module.PersonEditPage })))

function ProtectedApp() {
  const { configured, loading, session, recovering } = useAuth()
  const location = useLocation()

  if (!configured) return <LoginPage />
  if (loading) return <LoadingState fullPage label="Checking your session…" />
  if (recovering) return <Navigate to={`/reset-password${location.search}${location.hash}`} replace />
  if (!session) return <Navigate to="/login" state={{ from: location }} replace />

  return (
    <AppShell>
      <Suspense fallback={<LoadingState label="Loading workspace…" />}>
        <Routes>
          <Route path="/overview" element={<OverviewPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/plots" element={<PlotsPage />} />
          <Route path="/plots/new" element={<NewRecordPage recordType="plot" />} />
          <Route path="/plots/:plotId" element={<PlotDetailPage />} />
          <Route path="/people" element={<PeoplePage />} />
          <Route path="/people/new/deceased" element={<NewRecordPage recordType="deceased" />} />
          <Route path="/people/new/owner" element={<NewRecordPage recordType="owner" />} />
          <Route path="/people/deceased/:personId" element={<PersonEditPage recordType="deceased" />} />
          <Route path="/people/owner/:personId" element={<PersonEditPage recordType="owner" />} />
          <Route path="*" element={<Navigate to="/overview" replace />} />
        </Routes>
      </Suspense>
    </AppShell>
  )
}

export default function App() {
  const { loading, session, recovering } = useAuth()
  const location = useLocation()
  return (
    <Routes>
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/login" element={loading ? <LoadingState fullPage label="Checking your session…" /> : recovering ? <Navigate to={`/reset-password${location.search}${location.hash}`} replace /> : session ? <Navigate to="/overview" replace /> : <LoginPage />} />
      <Route path="/*" element={<ProtectedApp />} />
    </Routes>
  )
}
